import { useEffect, useRef } from 'react';

import { Box } from '@mui/material';

import {
    AssistantLoadingSkeleton,
    AssistantMessageBubble,
    ErrorMessageBubble,
    UserMessageBubble,
} from './MessageBubbles';
import { ChatMessage } from './utils';

const GREETING_MESSAGE =
    "Hi! I'm Sara, your AI writing assistant. Tell me what you'd like to write and I'll help you craft it.";

export type Props = {
    messages: ChatMessage[];
    errorMessage?: string | null;
    isLoading?: boolean;
    onRetry?: () => void;
};

/**
 * Scrollable transcript for Ask Sara. Always renders a greeting as the first message.
 * Assistant: left-aligned + sparkle, no fill, with feedback action bar below.
 * User: right-aligned mint bubble.
 */
const MessagesDisplay = ({ messages, errorMessage, isLoading = false, onRetry }: Props) => {
    const lastAssistantIndex = messages.reduce<number>((acc, msg, i) => (msg.role === 'assistant' ? i : acc), -1);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const hasMountedRef = useRef(false);

    // Scroll the transcript to the bottom whenever content changes (DOM side effect, not derivable from state).
    // scrollTop is used instead of scrollIntoView to avoid triggering horizontal scroll on ancestor elements.
    // Use instant jump on first render so pre-loaded history doesn't animate past the user.
    useEffect(() => {
        const el = scrollContainerRef.current;
        if (!el) return;
        if (hasMountedRef.current) {
            el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
        } else {
            el.scrollTop = el.scrollHeight;
        }
        hasMountedRef.current = true;
    }, [messages, isLoading, errorMessage]);

    return (
        <Box
            ref={scrollContainerRef}
            aria-label="Chat messages"
            data-testid="copy-generator-chat-messages-placeholder"
            role="region"
            sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                px: 2,
                py: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                width: '100%',
            }}
        >
            <Box data-testid="copy-generator-message-greeting" aria-label="Assistant message" role="article">
                <AssistantMessageBubble content={GREETING_MESSAGE} />
            </Box>

            {messages.map((message, index) => {
                const key = `${message.id}-${index}`;
                if (message.role === 'assistant') {
                    const isLastAssistant = index === lastAssistantIndex;
                    return (
                        <Box
                            key={key}
                            data-testid={`copy-generator-message-assistant-${index}`}
                            aria-label="Assistant message"
                            role="article"
                        >
                            <AssistantMessageBubble
                                content={message.content}
                                isLoading={isLoading}
                                onRetry={isLastAssistant ? onRetry : undefined}
                                messageId={message.id}
                                initialFeedback={message.feedback}
                            />
                        </Box>
                    );
                }

                return (
                    <Box
                        key={key}
                        data-testid={`copy-generator-message-user-${index}`}
                        aria-label="Your message"
                        role="article"
                    >
                        <UserMessageBubble content={message.content} />
                    </Box>
                );
            })}

            {isLoading && <AssistantLoadingSkeleton />}

            {errorMessage && (
                <Box data-testid="copy-generator-message-error" aria-label="Error message" role="alert">
                    <ErrorMessageBubble content={errorMessage} />
                </Box>
            )}
        </Box>
    );
};

export default MessagesDisplay;
